import {
  Box,
  Button,
  Divider,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { range } from 'lodash-es';
import { PiPencil, PiTrash } from 'react-icons/pi';
import Drawer from '@/components/drawer/Drawer';
import { calculateCogsInEUR, groupCogsByCurrency } from '@/lib/cogs';
import { formatPrice } from '@/lib/helpers';
import { useConfirmation } from '@/hooks/useConfirmation';
import {
  getServiceBlockTiers,
  IServiceBlock,
  useBlocksActions,
} from '@/models/commercial-proposals/blocks.store';
import {
  RENEWAL_PERIOD_LABELS,
  TService,
  useChildServices,
  useIncludedAgentServices,
  useSaveService,
} from '@/models/services';
import { useRates } from '@/models/rates';
import ServiceEditor from '@/pages/products/services/ServiceEditor';
import { IPriceCellConfig } from '../ServicePriceCell/ServicePriceCellSettings';

export interface IServiceCellConfig {
  showDescription: boolean;
  showChildServices: boolean;
  customDescription: string;
  hidden?: boolean;
}

function EditServiceDrawer({ service }: { service: TService }) {
  const [opened, { open, close }] = useDisclosure(false);
  const { mutate: saveService } = useSaveService();
  return (
    <>
      <Drawer opened={opened} onClose={close} size={1000}>
        <Box p="md">
          <ServiceEditor
            title={service.name}
            titleOrder={1}
            service={service}
            allowDelete={false}
            onSave={(updatedService) => {
              saveService(updatedService as TService, {
                onSuccess: () => {
                  notifications.show({
                    title: 'Service successfully saved',
                    message: `Service "${updatedService.name}" was saved successfully.`,
                    color: 'green',
                    position: 'top-right',
                  });
                },
              });
            }}
          />
        </Box>
      </Drawer>
      <Button
        size="xs"
        variant="light"
        onClick={open}
        leftSection={<PiPencil size={16} />}
      >
        Edit service
      </Button>
    </>
  );
}

function ServiceInfo({ record }: { record: TService }) {
  const { data: agentServices } = useIncludedAgentServices(record.id);
  const { data: rates } = useRates();
  const renewal = record.renewal;

  const cogsByCurrency = agentServices
    ? groupCogsByCurrency(agentServices)
    : {};
  const cogsEntries = Object.entries(cogsByCurrency);
  const cogsInEUR =
    rates && cogsEntries.length ? calculateCogsInEUR(cogsByCurrency, rates) : 0;
  const isMultiCurrency =
    cogsEntries.length > 1 ||
    (cogsEntries.length === 1 && cogsEntries[0][0] !== 'EUR');

  return (
    <Table fz="xs" withRowBorders={false} verticalSpacing={2}>
      <Table.Tbody>
        <Table.Tr>
          <Table.Td c="dimmed" w={80}>
            Price
          </Table.Td>
          <Table.Td fw={500}>
            {formatPrice(record.price, record.currency)}
          </Table.Td>
        </Table.Tr>
        {renewal?.isRenewable && (
          <>
            <Table.Tr>
              <Table.Td c="dimmed">Renewal</Table.Td>
              <Table.Td fw={500}>
                {RENEWAL_PERIOD_LABELS[renewal.period] ?? 'Yes'}
                {renewal.renewalPrice != null &&
                  ` · ${formatPrice(renewal.renewalPrice, record.currency)}`}
              </Table.Td>
            </Table.Tr>
            {renewal.offsetDays != null && renewal.offsetDays > 0 && (
              <Table.Tr>
                <Table.Td c="dimmed">Offset</Table.Td>
                <Table.Td fw={500}>{renewal.offsetDays} days</Table.Td>
              </Table.Tr>
            )}
            {renewal.fineType && (
              <Table.Tr>
                <Table.Td c="dimmed">Fine</Table.Td>
                <Table.Td fw={500}>
                  {renewal.fineType === 'static' ? 'Static' : 'Incremental'} ·{' '}
                  {formatPrice(renewal.fineAmount ?? 0, record.currency)}
                </Table.Td>
              </Table.Tr>
            )}
          </>
        )}
        {cogsEntries.length > 0 && (
          <Table.Tr>
            <Table.Td c="dimmed">COGS</Table.Td>
            <Table.Td fw={500}>
              {cogsEntries
                .map(([cur, amount]) => formatPrice(amount, cur))
                .join(' + ')}
              {isMultiCurrency && cogsInEUR > 0 && (
                <Text span fz="xs" c="dimmed">
                  {' '}
                  ({formatPrice(cogsInEUR, 'EUR')})
                </Text>
              )}
            </Table.Td>
          </Table.Tr>
        )}
      </Table.Tbody>
    </Table>
  );
}

export default function ServiceNameCellSettings({
  record,
  config,
  service,
  block,
  onChange,
}: {
  record: TService;
  config: IServiceCellConfig;
  service: IServiceBlock['services'][number];
  block: IServiceBlock;
  onChange: (config: IServiceCellConfig) => void;
}) {
  const { update } = useBlocksActions();
  const { confirm, ConfirmationDialog } = useConfirmation();
  const { data: childRecords } = useChildServices(service.serviceId);

  const removeService = async () => {
    const confirmed = await confirm({
      title: 'Remove Service',
      message: `Are you sure you want to remove "${record.name}" service?`,
      confirmButtonText: 'Remove',
      cancelButtonText: 'Cancel',
    });

    if (confirmed) {
      update({
        ...block,
        services: block.services.filter(
          (s) =>
            !(
              (s.serviceId === service.serviceId &&
                s.weight === service.weight) ||
              s.parentId === service.serviceId
            ),
        ),
      });
    }
  };

  return (
    <Stack>
      <Text size="xs" fw={500}>
        {record.name}
      </Text>
      <ServiceInfo record={record} />
      <Divider />
      {childRecords && childRecords.length > 0 && (
        <Stack gap="0">
          <Switch
            label="Show child services"
            size="xs"
            mb={'xs'}
            checked={config.showChildServices}
            onChange={(event) => {
              const showChildServices = event.currentTarget.checked;

              let services = [...block.services];
              if (showChildServices === true) {
                const children = childRecords.map(
                  (childRecord) =>
                    ({
                      blockUuid: block.uuid,
                      serviceId: childRecord.id,
                      weight: 0,
                      parentId: service.serviceId,
                      config: range(getServiceBlockTiers(block.type)).reduce(
                        (acc, tier) => {
                          acc[tier] = {
                            included: false,
                            displayIcon: false,
                          } as IPriceCellConfig;
                          return acc;
                        },
                        {} as Record<number, IPriceCellConfig>,
                      )!,
                      price: {
                        amount: childRecord.price,
                        currency: childRecord.currency,
                      },
                    }) as IServiceBlock['services'][number],
                );
                services.splice(service.weight + 1, 0, ...children);
                services.forEach((service, idx) => {
                  service.weight = idx;
                });
              } else {
                services = services.filter(
                  (s) => s.parentId !== service.serviceId,
                );
              }

              update({
                ...block,
                services,
              });

              onChange({
                ...config,
                showChildServices,
              });
            }}
          />
          {childRecords.map((childRecord) => (
            <Text size="xs" key={childRecord.id} c="dimmed">
              - {childRecord.name}
            </Text>
          ))}
        </Stack>
      )}
      <Stack gap={0}>
        <Switch
          label="Show description"
          size="xs"
          checked={config.showDescription}
          onChange={(event) => {
            onChange({
              ...config,
              showDescription: event.currentTarget.checked,
            });
          }}
        />
        {config.showDescription && (
          <Textarea
            size="xs"
            label="Description"
            value={config.customDescription || ''}
            minRows={2}
            maxRows={12}
            autosize
            resize="horizontal"
            onChange={(e) => {
              onChange({
                ...config,
                customDescription: e.currentTarget.value,
              });
            }}
          />
        )}
      </Stack>
      <Switch
        label="Hidden (renewal only)"
        description="Hide from customer view and invoice, keep in renewals"
        size="xs"
        checked={!!config.hidden}
        onChange={(event) => {
          onChange({
            ...config,
            hidden: event.currentTarget.checked,
          });
        }}
      />
      <Box>
        <EditServiceDrawer service={record} />
      </Box>
      <Box>
        <Button
          size="xs"
          color="red"
          variant="light"
          onClick={removeService}
          leftSection={<PiTrash size={16} />}
        >
          Remove service
        </Button>
      </Box>
      <ConfirmationDialog />
    </Stack>
  );
}
